/**
 * Nexita (nexita.fr) — slim checkout client + ATC tracker
 * ---------------------------------------------------------------
 * Runs on the storefront (the main store). Does three things:
 *
 *   1. Captures ad attribution (gclid, wbraid, gbraid, utm_*, tbclid,
 *      tb_clid) from the URL into sessionStorage so it survives page
 *      navigation. Also generates a session id (_rd_sid) that is sent
 *      with every tracking event, so the CRM can join the funnel:
 *      add-to-cart → checkout redirect → order.
 *
 *   2. COUNTS ADD-TO-CART CLICKS: a fire-and-forget beacon to
 *      ENDPOINT/track/atc on every /cart/add. Covered three ways so it
 *      works with any theme: patched window.fetch (modern themes),
 *      patched XMLHttpRequest (older/jQuery themes), and capture-phase
 *      form-submit + button-click listeners (everything else). An
 *      800ms dedupe window keeps one add from counting 2–3×.
 *
 *   3. Intercepts checkout clicks ANYWHERE on the page — including the
 *      checkout button inside the cart slider/drawer, even though the
 *      drawer is created after the page loads (we listen at the page
 *      level, not on the button itself). On click: fetches the cart,
 *      asks the resolver for the redirect URL (sending the cart total
 *      + session id along), and redirects there. Any failure falls back
 *      to the store's normal /checkout page.
 *
 * Everything sensitive (token, destination list, quotas, rotation
 * logic, variant maps) lives on the server. The browser learns only
 * its own URL.
 *
 * Install (for a NEW store):
 *   1. Shopify admin -> Themes -> Edit code -> Assets -> redirect.js ->
 *      paste this file (set ENDPOINT below) -> Save.
 *   2. Edit code -> layout/theme.liquid -> just above </body> add:
 *        <script src="{{ 'redirect.js' | asset_url }}"></script>
 *      -> Save.
 */
(function () {
  "use strict";

  // ==== config ============================================================
  // Your deployed resolver endpoint
  var ENDPOINT = "https://nexita-checkout-resolver.chillvora.workers.dev";
  // Includes reading Shopify's cart and asking the resolver. A slow network
  // must not leave the checkout button waiting indefinitely.
  var CHECKOUT_TIMEOUT_MS = 3000;

  // Ad attribution params to capture and forward.
  var TK = ["gclid", "wbraid", "gbraid", "utm_source", "utm_medium",
            "utm_campaign", "utm_content", "utm_term", "tbclid", "tb_clid"];

  // Checkout-looking elements we intercept. Includes the usual cart-drawer
  // checkout buttons AND submit buttons inside checkout forms.
  var SELECTOR = 'a[href*="/checkout"],button[name="checkout"],input[name="checkout"],' +
                 '[data-action="checkout"],.cart__checkout-button,#checkout,' +
                 'form[action*="/checkout"] button[type="submit"],' +
                 'form[action*="/checkout"] input[type="submit"]';

  // Add-to-cart-looking elements (the click fallback; the fetch/XHR patch
  // below catches the actual /cart/add request in most themes).
  var ATC_SELECTOR = 'button[name="add"],input[name="add"],' +
                     '[data-action="add-to-cart"],[data-action="add_item"],' +
                     '#AddToCart,#add-to-cart,.product-form__submit,' +
                     '.product-form__cart-submit,.add-to-cart,.add-to-cart-button';

  // ==== attribution capture (runs on load) =================================
  try {
    var u = new URLSearchParams(window.location.search);
    TK.forEach(function (k) {
      var v = u.get(k);
      if (v) { try { sessionStorage.setItem("_rd_" + k, v); } catch (e) {} }
    });
  } catch (e) {}

  function getTracked() {
    // Current URL value wins; sessionStorage is the fallback so attribution
    // survives navigation away from the landing page.
    var cur = new URLSearchParams(window.location.search);
    var out = {};
    TK.forEach(function (k) {
      var v = cur.get(k);
      if (!v) { try { v = sessionStorage.getItem("_rd_" + k); } catch (e) {} }
      if (v) out[k] = v;
    });
    return out;
  }

  // ==== session id (joins atc → redirect → order in the CRM) ==============
  function getSid() {
    try {
      var s = sessionStorage.getItem("_rd_sid");
      if (s) return s;
      s = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem("_rd_sid", s);
      return s;
    } catch (e) { return ""; }
  }

  function getLocale() {
    // Preserve /xx-xx/ locale prefixes on the destination URL.
    var m = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(?:\/|$)/);
    return m ? "/" + m[1] : "";
  }

  function localCheckout() { window.location.href = "/checkout"; }

  function newAttemptId() {
    // This is an idempotency key and a cancellation capability. Keep it
    // unguessable and scoped to this click, never to the shopper's session.
    var c = window.crypto;
    if (!c) throw new Error("Secure randomness unavailable");
    if (typeof c.randomUUID === "function") return c.randomUUID();
    var bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    return Array.prototype.map.call(bytes, function (b, i) {
      return ([4, 6, 8, 10].indexOf(i) !== -1 ? "-" : "") +
        ("0" + b.toString(16)).slice(-2);
    }).join("");
  }

  function cancelAttempt(attemptId, reason) {
    // Fire-and-forget: do not hold up the customer's local checkout while
    // the resolver reconciles an allocation whose reply was never used.
    try {
      var body = JSON.stringify({ attempt_id: attemptId, reason: reason });
      var url = ENDPOINT + "/resolve/cancel";
      var blob = new Blob([body], { type: "text/plain" });
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: body,
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  // ==== add-to-cart tracking (fire-and-forget beacon) ======================
  var lastAtc = 0; // dedupe window — one add can fire click+submit+fetch
  function trackAtc(variant) {
    var now = Date.now();
    if (now - lastAtc < 800) return;
    lastAtc = now;
    try {
      var body = JSON.stringify({
        sid: getSid(),
        v: variant || "",
        qs: getTracked()
      });
      var blob = new Blob([body], { type: "text/plain" });
      if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT + "/track/atc", blob)) return;
      fetch(ENDPOINT + "/track/atc", {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: body,
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  // Best-effort variant id out of an /cart/add request body.
  function variantFromBody(b) {
    try {
      if (typeof b === "string") {
        try {
          var j = JSON.parse(b);
          return pickVariant(j);
        } catch (e) {}
        return String(new URLSearchParams(b).get("id") || "");
      }
      if (typeof FormData !== "undefined" && b instanceof FormData) {
        return String(b.get("id") || "");
      }
      if (typeof URLSearchParams !== "undefined" && b instanceof URLSearchParams) {
        return String(b.get("id") || "");
      }
      if (b && typeof b === "object") return pickVariant(b);
    } catch (e) {}
    return "";
  }
  function pickVariant(j) {
    try {
      if (Array.isArray(j.items) && j.items.length) {
        return String(j.items[0].id || j.items[0].variant_id || "");
      }
      return String(j.id || j.variant_id || "");
    } catch (e) { return ""; }
  }

  // signal 1: modern themes POST /cart/add via fetch()
  try {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === "string" ? input
                : (input && input.url) || "";
        var method = String((init && init.method) ||
                            (input && input.method) || "get").toUpperCase();
        if (method === "POST" && /\/cart\/add(\.js|\.json)?(\?|$)/.test(url)) {
          trackAtc(variantFromBody(init && init.body));
        }
      } catch (e) {}
      return _fetch.apply(this, arguments);
    };
  } catch (e) {}

  // signal 2: older/jQuery themes POST /cart/add via XMLHttpRequest
  try {
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._rd = { m: String(method || "").toUpperCase(), u: String(url || "") };
      return _open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this._rd && this._rd.m === "POST" &&
            /\/cart\/add(\.js|\.json)?(\?|$)/.test(this._rd.u)) {
          trackAtc(variantFromBody(body));
        }
      } catch (e) {}
      return _send.apply(this, arguments);
    };
  } catch (e) {}

  // signal 3a: plain form submits to /cart/add (any theme)
  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (f instanceof HTMLFormElement && /\/cart\/add/.test(f.action || "")) {
      var v = "";
      try { v = String(new FormData(f).get("id") || ""); } catch (err) {}
      trackAtc(v);
    }
  }, true);

  // signal 3b: add-to-cart button clicks (AJAX themes with no form submit)
  document.addEventListener("click", function (e) {
    if (!(e.target instanceof Element)) return;
    var b = e.target.closest(ATC_SELECTOR);
    if (!b) return;
    var v = "";
    try {
      var f = b.closest("form");
      if (f) v = String(new FormData(f).get("id") || "");
    } catch (err) {}
    trackAtc(v);
  }, true);

  // ==== the redirect (shared by both listeners below) =====================
  var busy = false; // guards against fast double-clicks double-counting
  // A cached page keeps its JavaScript state when the shopper comes back.
  // Its earlier attempt completed or was abandoned on pagehide; the next
  // intentional checkout needs a new attempt instead of the old busy guard.
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) busy = false;
  });
  function intercept() {
    if (busy) return;
    busy = true;

    var attemptId;
    try { attemptId = newAttemptId(); }
    catch (e) { localCheckout(); return; }
    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var finished = false;
    var resolverStarted = false;
    var timer = setTimeout(function () { fallback("browser_timeout"); }, CHECKOUT_TIMEOUT_MS);

    function stop() {
      clearTimeout(timer);
      window.removeEventListener("pagehide", onPageHide);
    }
    function abandon(reason) {
      if (finished) return false;
      finished = true;
      stop();
      if (controller) controller.abort();
      if (resolverStarted) cancelAttempt(attemptId, reason);
      return true;
    }
    function fallback(reason) {
      if (abandon(reason)) localCheckout();
    }
    function onPageHide() { abandon("page_abandoned"); }
    window.addEventListener("pagehide", onPageHide);

    var cartOptions = { credentials: "same-origin" };
    if (controller) cartOptions.signal = controller.signal;
    fetch("/cart.js", cartOptions)
      .then(function (r) {
        if (finished) return;
        if (!r.ok) throw new Error("Cart request failed");
        return r.json();
      })
      .then(function (cart) {
        if (finished) return;
        var items = (cart.items || []).map(function (i) {
          return { v: String(i.variant_id), q: i.quantity };
        });
        if (!items.length) { fallback("empty_cart"); return; }

        var options = {
          method: "POST",
          // text/plain on purpose: skips the browser's preflight check,
          // saving a round trip. The body is still JSON, just unlabeled.
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify({
            attempt_id: attemptId,
            items: items,
            locale: getLocale(),
            qs: getTracked(),
            sid: getSid(),
            total: cart.total_price || 0   // cart value in cents (CRM)
          })
        };
        if (controller) options.signal = controller.signal;
        resolverStarted = true;
        return fetch(ENDPOINT, options)
          .then(function (r) {
            if (finished) return;
            if (!r.ok) throw new Error("Resolver request failed");
            return r.json();
          })
          .then(function (j) {
            if (finished) return;
            if (!j || typeof j.url !== "string" || !j.url) {
              fallback("resolver_fallback");
              return;
            }
            var target = new URL(j.url);
            if (target.protocol !== "https:" || target.username || target.password ||
                !/^\/(?:[a-z]{2}-[a-z]{2}\/)?cart\/\d{1,20}:[1-9]\d*(?:,\d{1,20}:[1-9]\d*)*$/.test(target.pathname)) {
              fallback("invalid_destination");
              return;
            }
            finished = true;
            stop();
            try { window.location.replace(target.href); }
            catch (e) { finished = false; fallback("navigation_error"); }
          });
      })
      .catch(function () { fallback("request_failed"); });
  }

  // ==== interception, part 1: checkout clicks ==============================
  // A document-level listener in capture phase, so it fires no matter which
  // element is clicked — including buttons inside the cart slider that are
  // added to the page AFTER load.
  document.addEventListener("click", function (e) {
    if (!(e.target instanceof Element)) return;
    var b = e.target.closest(SELECTOR);
    if (!b) return;
    e.preventDefault();  // stop the normal checkout
    e.stopPropagation();
    intercept();
  }, true);

  // ==== interception, part 2: checkout form submits ========================
  // Some themes' cart drawers use a plain submit button inside a form that
  // posts to /checkout. Those never produce a matching "click" target, so we
  // also watch for the form submission itself.
  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (f instanceof HTMLFormElement && /\/checkout/.test(f.action || "")) {
      e.preventDefault();
      e.stopPropagation();
      intercept();
    }
  }, true);
})();
